import { useCallback, type ReactNode } from 'react'

import { trackClientEvent } from 'analytics/analytics'
import { getListName } from 'analytics/enhanced-ecommerce'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import {
  OfferListEvents,
  OfferListEventsProvider,
  type OfferListEventHandler,
} from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'

import CruisePageCruiseLinesSection from './CruisePageCruiseLinesSection/CruisePageCruiseLinesSection'
import CruisePageDealsTileCarousel from './CruisePageDealsTileCarousel'
import CruisePageDepartureFromCarousel from './CruisePageDepartureFromCarousel/CruisePageDepartureFromCarousel'
import CruisePageDeparturesSection from './CruisePageDeparturesSection'
import CruisePageDestinationsSection from './CruisePageDestinationsSection'
import CruisePageCruiseTourCarousel from './CruisePageCruiseTourCarousel'
import CruisePageExpeditionCruisesCarousel from './CruisePageExpeditionCruisesCarousel'
import CruisePageLongHaulOceanCruisesCarousel from './CruisePageLongHaulOceanCruisesCarousel'
import CruisePageLuxuryPremiumCruisesCarousel from './CruisePageLuxuryPremiumCruisesCarousel'
import CruisePageLuxPlusOffersCarousel from './CruisePageLuxPlusOffersCarousel'
import CruisePageRiverCruisesCarousel from './CruisePageRiverCruisesCarousel'
import CruisePageRoyalCruisesCarousel from './CruisePageRoyalCruisesCarousel'
import CruisePageShortTermCruisesCarousel from './CruisePageShortTermCruisesCarousel'
import CruisePageSpecialOffersCarousel from './CruisePageSpecialOffersCarousel'
import CruisePageTopOffersCarousel from './CruisePageTopOffersCarousel'
import { CruisePageCarouselSection } from './useCruisePageContentOrder'

const CRUISES_CAROUSEL_ITEM_CLICKED_OPTIMIZELY_EVENT_ID = '4719270064553984'
const CRUISES_CAROUSEL_ITEM_CLICKED_OPTIMIZELY_EVENT_KEY = 'cruises_carousel_item_clicked'

const CONTENT_SECTION_LIST_NAMES: Record<CruisePageCarouselSection, string> = {
  [CruisePageCarouselSection.Deals]: 'Hero Carousel',
  [CruisePageCarouselSection.TopOffers]: 'Todays Top Cruises',
  [CruisePageCarouselSection.LuxPlusOffers]: 'LuxPlus Offers',
  [CruisePageCarouselSection.CruiseLines]: 'Featured Cruise Lines',
  [CruisePageCarouselSection.Destinations]: 'Popular cruise destinations',
  [CruisePageCarouselSection.DepartingFrom]: 'Departing From',
  [CruisePageCarouselSection.RoyalCaribbean]: 'Royal Caribbean Hot Sailings',
  [CruisePageCarouselSection.Departures]: 'Top Ports to Start Your Adventure',
  [CruisePageCarouselSection.ShortTerm]: 'Short Term Cruises',
  [CruisePageCarouselSection.LongHaulOcean]: 'Long-haul Ocean Cruises',
  [CruisePageCarouselSection.LuxuryPremium]: 'Luxury & Premium Cruises',
  [CruisePageCarouselSection.River]: 'River Cruises',
  [CruisePageCarouselSection.BestValue]: 'Best Value',
  [CruisePageCarouselSection.Expedition]: 'Expedition Cruises',
  [CruisePageCarouselSection.CruiseTour]: 'Cruise Tour',
}

function trackCruisesCarouselItemClick() {
  trackClientEvent({
    subject: CRUISES_CAROUSEL_ITEM_CLICKED_OPTIMIZELY_EVENT_KEY,
    action: 'clicked',
    category: 'logging',
    type: 'operational',
    optimizelyEventId: CRUISES_CAROUSEL_ITEM_CLICKED_OPTIMIZELY_EVENT_ID,
    optimizelyEventKey: CRUISES_CAROUSEL_ITEM_CLICKED_OPTIMIZELY_EVENT_KEY,
  })
}

interface Props {
  contentName: CruisePageCarouselSection
}

export default function CruisePageContentSection({ contentName }: Props) {
  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', CONTENT_SECTION_LIST_NAMES[contentName]),
  )

  const onCarouselListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (
      dispatchAction.type === OfferListEvents.productClick ||
      dispatchAction.type === OfferListEvents.destinationClick
    ) {
      trackCruisesCarouselItemClick()
    }
  }, [])

  let content: ReactNode

  switch (contentName) {
    case CruisePageCarouselSection.Deals:
      content = (
        <>
          <ScrollAnchor id="deals-carousel" />
          <CruisePageDealsTileCarousel onCarouselItemClick={trackCruisesCarouselItemClick} />
        </>
      )
      break

    case CruisePageCarouselSection.TopOffers:
      content = <CruisePageTopOffersCarousel />
      break

    case CruisePageCarouselSection.LuxPlusOffers:
      content = <CruisePageLuxPlusOffersCarousel />
      break

    case CruisePageCarouselSection.CruiseLines:
      content = <CruisePageCruiseLinesSection onCarouselItemClick={trackCruisesCarouselItemClick} />
      break

    case CruisePageCarouselSection.Destinations:
      content = <CruisePageDestinationsSection />
      break

    case CruisePageCarouselSection.DepartingFrom:
      content = <CruisePageDepartureFromCarousel />
      break

    case CruisePageCarouselSection.RoyalCaribbean:
      content = <CruisePageRoyalCruisesCarousel />
      break

    case CruisePageCarouselSection.Departures:
      content = <CruisePageDeparturesSection />
      break

    case CruisePageCarouselSection.ShortTerm:
      content = <CruisePageShortTermCruisesCarousel />
      break

    case CruisePageCarouselSection.LongHaulOcean:
      content = <CruisePageLongHaulOceanCruisesCarousel />
      break

    case CruisePageCarouselSection.LuxuryPremium:
      content = <CruisePageLuxuryPremiumCruisesCarousel />
      break

    case CruisePageCarouselSection.River:
      content = <CruisePageRiverCruisesCarousel />
      break

    case CruisePageCarouselSection.BestValue:
      content = <CruisePageSpecialOffersCarousel />
      break

    case CruisePageCarouselSection.Expedition:
      content = <CruisePageExpeditionCruisesCarousel />
      break

    case CruisePageCarouselSection.CruiseTour:
      content = <CruisePageCruiseTourCarousel />
      break
  }

  return (
    <OfferListEventsProvider tracking={tracking} onListEvent={onCarouselListEvent}>
      {content}
    </OfferListEventsProvider>
  )
}
